import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { TranslateModule } from '@ngx-translate/core';
import { faTimes, faCheck, faRotateLeft, faSliders } from '@fortawesome/free-solid-svg-icons';
import { ButtonComponent } from '../button/button.component';
import { Configuration } from '../../services/configurations.service';

type OcrOverride = Record<string, any>;

interface OcrFeaturesForm {
  layoutAnalysis: string;
  inputType: string;
  ocrType: string;
  outputType: string;
  textType: string;
  ocrCrop: boolean;
  autoCut: boolean;
  enhanceDocument: boolean;
  sortOcr: boolean;
  adjustRotation: string;
  splitPdf: boolean;
}

function emptyOcrForm(): OcrFeaturesForm {
  return {
    layoutAnalysis: '',
    inputType: '',
    ocrType: '',
    outputType: '',
    textType: 'mix',
    ocrCrop: false,
    autoCut: false,
    enhanceDocument: false,
    sortOcr: false,
    adjustRotation: '',
    splitPdf: false,
  };
}

@Component({
  selector: 'app-modal-ocr-override',
  standalone: true,
  imports: [CommonModule, FormsModule, FontAwesomeModule, TranslateModule, ButtonComponent],
  templateUrl: './modal-ocr-override.component.html',
  styleUrls: ['./modal-ocr-override.component.scss']
})
export class ModalOcrOverrideComponent {
  faTimes = faTimes;
  faCheck = faCheck;
  faRotateLeft = faRotateLeft;
  faSliders = faSliders;

  @Input() baseConfig: Configuration | null = null;

  @Input() set initialOverride(val: OcrOverride | null) {
    this._initialOverride = val;
  }
  private _initialOverride: OcrOverride | null = null;

  @Input() set show(val: boolean) {
    const wasClosed = !this._show && val;
    this._show = val;
    if (wasClosed) {
      this.initForm();
    }
  }
  get show(): boolean {
    return this._show;
  }
  private _show = false;

  form: OcrFeaturesForm = emptyOcrForm();

  @Output() apply = new EventEmitter<OcrOverride>();
  @Output() restore = new EventEmitter<void>();
  @Output() close = new EventEmitter<void>();

  private initForm(): void {
    const base = this.baseConfig?.value || {};
    this.form = this.normalize({ ...base, ...(this._initialOverride || {}) });
  }

  private normalize(src: OcrOverride): OcrFeaturesForm {
    return {
      layoutAnalysis: src['layoutAnalysis'] != null ? String(src['layoutAnalysis']) : '',
      inputType: src['inputType'] ?? '',
      ocrType: src['ocrType'] ?? '',
      outputType: src['outputType'] ?? '',
      textType: src['textType'] ?? 'mix',
      ocrCrop: src['ocrCrop'] ?? false,
      autoCut: src['autoCut'] ?? false,
      enhanceDocument: src['enhanceDocument'] ?? false,
      sortOcr: src['sortOcr'] ?? false,
      adjustRotation: typeof src['adjustRotation'] === 'string' ? src['adjustRotation'] : '',
      splitPdf: src['splitPdf'] ?? false,
    };
  }

  restoreOriginal(): void {
    this.restore.emit();
  }

  onApply(): void {
    this.apply.emit({ ...this.form } as OcrOverride);
  }

  onClose(): void {
    this.close.emit();
  }
}
